/**
 * Getting a new build onto a device that already has one.
 *
 * A PWA's whole design is to open instantly from cache, which means the copy running on
 * a phone is by default the copy that was current when it was last installed. The shell
 * is stale-while-revalidate, so a new build does arrive — but only into the cache. It
 * does not reach the screen until the page is loaded again, and an installed app is
 * rarely loaded again: it is resumed. A device can sit several builds behind and look
 * perfectly healthy, and every bug report from it is about code nobody is running any
 * more.
 *
 * So: when a replacement worker takes over, reload. Once, and never during a sit — the
 * one moment when losing the page actually costs something. If a sit is running, the
 * reload waits for it to end.
 */

import { effect, signal } from '@preact/signals';

/** True while audio is actually playing. Nothing may reload the page while it is. */
export const sitInProgress = signal(false);

/** A new build has arrived and is waiting for the sit to finish. */
export const updateWaiting = signal(false);

export function watchForUpdate(reloadNow: () => void = () => location.reload()): () => void {
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return () => {};

  // A first registration claims the page too, and that is not an update — there is
  // nothing stale to replace, and reloading would put a flash in front of a first run.
  const hadController = !!navigator.serviceWorker.controller;
  let reloaded = false;

  const reload = () => {
    if (reloaded) return;
    if (sitInProgress.value) { updateWaiting.value = true; return; }
    reloaded = true;
    reloadNow();
  };

  const onChange = () => { if (hadController) reload(); };
  navigator.serviceWorker.addEventListener('controllerchange', onChange);

  // Take the deferred reload the moment the sit is over.
  const stop = effect(() => { if (updateWaiting.value && !sitInProgress.value) reload(); });

  return () => {
    navigator.serviceWorker.removeEventListener('controllerchange', onChange);
    stop();
  };
}
